import { Injectable } from "@angular/core";
import { map, Observable } from "rxjs";
import {
  andAllWhere,
  andWhere,
  buildQuery,
  CombinedFilter,
  FilterFn,
  noop,
  Normalizers,
  Ops,
  orderBy,
  OrderVariant,
  orWhere,
  page,
  select,
  where,
} from "@albatrosdeveloper/ave-utils-npm/lib/utils/query.util";
import { HttpClient } from "@angular/common/http";
import {
  IPagination,
  IPaginationResult,
  IRequestResult,
} from "../../../../common/interfaces";
import { environment as env } from "../../../../../environments/environment.prod";
import { StatusEnum } from "@albatrosdeveloper/ave-models-package/lib/enums";
import { AbstractService } from "../../../../services/abstract.service";
import SubRouteAttributes from "@albatrosdeveloper/ave-models-package/lib/schemas/routing/sub-route/sub-route.entity";
import { ICreateSubRoute } from "./interfaces/create-sub-route.interface";
import { IUpdateSubRoute } from "./interfaces/update-sub-route.interface";
import { RequestOptions } from "app/common/interfaces/options.interface";
import { IFilters } from "../../filters/interfaces/filterInterface";
const apiUrl = env.API_ROUTING_URL;
const moduleName = "sub-route";
export const SUB_ROUTE_IMPORT_FILE_FULL_URL = apiUrl + `api/${moduleName}/upload`;

@Injectable({
  providedIn: "root",
})
export class SubRouteService extends AbstractService<
  SubRouteAttributes,
  ICreateSubRoute,
  Partial<IUpdateSubRoute>
> {
  /** The last filter used to load the list */
  currentFilter: CombinedFilter<SubRouteAttributes>;

  constructor(http: HttpClient) {
    super(apiUrl, moduleName, http);
  }

  findByFilter(
    filter: CombinedFilter<SubRouteAttributes>,
    options?: RequestOptions
  ) {
    const API_URL: string =
      apiUrl +
      `api/${moduleName}/withCount?filter=${encodeURIComponent(JSON.stringify(filter))}`;
    const headers: Record<string, any> = {};
    // see order-customer-export for more info about this header
    // ctx: stands for context
    if (options?.contextHeader) headers["x-ave-ctx"] = options.contextHeader;
    return this.http.get<IPaginationResult<SubRouteAttributes>>(API_URL, {
      headers,
    });
  }

  /**
   * Obtiene rutas con paginación y filtros.
   *
   * Estrategia de búsqueda:
   * - Sin búsqueda: Un solo objeto con filtros base
   * - Con búsqueda: Ocho objetos (OR lógico) - busca en 'name', 'mainCollaborator.businessPartner.name',
   *   'responsibleBusinessPartner.name', 'responsibleBusinessPartner.externalKeys.code',
   *   'externalKeys.code', 'type', 'routeAreas.name', 'routes.name'
   *
   * Cada objeto del OR debe incluir TODAS las condiciones base para mantener consistencia.
   *
   * @param options Pagination options
   * @param dataToFilter Filters to apply
   * @param masterUserIds Master user IDs for filtering
   * @param modeTree Mode for tree display (disables pagination when set)
   * @returns Paginated data
   *
   * Search fields: name, mainCollaborator.businessPartner.name, responsibleBusinessPartner.name,
   *                responsibleBusinessPartner.externalKeys.code, externalKeys.code, type,
   *                routeAreas.name, routes.name
   * Array filters: routeId, salesOfficeId, routeAreaId, routeGroupId, status, functionCodes, seller
   * Single filters: companyId
   */
  findAllWithCount(
    options: IPagination<SubRouteAttributes>,
    dataToFilter: IFilters,
    // territoriesIds: RouteAttributes[],
    // routeAreaIds: RouteAreaAttributes[],
    masterUserIds: string[],
    // routeGroupIds: RouteGroupAttributes[],
    modeTree: "territory" | "salesOffice" | "group" | null = null
  ): Observable<IPaginationResult<SubRouteAttributes>> {
    // PRIORITY FILTER: If subRouteCodes is provided, ONLY filter by codes (ignore all other filters)
    if (dataToFilter?.subRouteCodes && dataToFilter.subRouteCodes.length > 0) {
      const query = buildQuery<SubRouteAttributes>(
        where("_deleted", Ops.eq(false)),
        andWhere(
          "externalKeys.code" as keyof SubRouteAttributes,
          Ops.in(...dataToFilter.subRouteCodes)
        ),
        orderBy(
          options.sort ?? "name",
          options.sortDirection === "ASC" ? OrderVariant.Asc : OrderVariant.Desc
        ),
        modeTree
          ? noop()
          : page(options.currentPage ?? 1, options.limitPerPage ?? 100)
      );

      this.currentFilter = query;

      const params = new URLSearchParams();
      params.set("filter", JSON.stringify(query));

      const API_URL = `${apiUrl}api/${moduleName}/withCount?${params.toString()}`;
      return this.http.get<IPaginationResult<SubRouteAttributes>>(API_URL);
    }

    const hasSearch = !!options.search;

    /**
     * Construye las condiciones base que SIEMPRE deben aplicarse.
     * Incluye: _deleted=false, filtros de compañía, ruta, oficina, área, grupo, estado, funciones, vendedor.
     *
     * @param useOrWhere - Si es true, inicia un nuevo objeto con orWhere (para el OR lógico)
     */
    const buildBaseFilters = (params: {
      useOrWhere: boolean;
    }): FilterFn<SubRouteAttributes>[] => {
      const deletedFilter: FilterFn<SubRouteAttributes> = params.useOrWhere
        ? orWhere("_deleted", Ops.eq(false)) // Inicia nuevo objeto en el array
        : where("_deleted", Ops.eq(false)); // Primer objeto del array

      const filters: FilterFn<SubRouteAttributes>[] = [deletedFilter];

      if (dataToFilter?.companyId) {
        filters.push(
          andWhere(
            "company.id" as keyof SubRouteAttributes,
            Ops.eq(dataToFilter.companyId)
          )
        );
      }

      if (dataToFilter?.routeId && dataToFilter.routeId.length > 0) {
        filters.push(
          andWhere(
            "routes.id" as keyof SubRouteAttributes,
            Ops.in(...dataToFilter.routeId)
          )
        );
      }

      if (
        dataToFilter?.salesOfficeId &&
        dataToFilter.salesOfficeId.length > 0
      ) {
        filters.push(
          andWhere(
            "salesOffice.id" as keyof SubRouteAttributes,
            Ops.in(...dataToFilter.salesOfficeId)
          )
        );
      }

      if (dataToFilter?.routeAreaId && dataToFilter.routeAreaId.length > 0) {
        filters.push(
          andWhere(
            "routeAreas.id" as keyof SubRouteAttributes,
            Ops.in(...dataToFilter.routeAreaId)
          )
        );
      }

      if (dataToFilter?.routeGroupId && dataToFilter.routeGroupId.length > 0) {
        filters.push(
          andWhere(
            "routeGroup.id" as keyof SubRouteAttributes,
            Ops.in(...dataToFilter.routeGroupId)
          )
        );
      }

      if (dataToFilter?.status && dataToFilter.status.length > 0) {
        filters.push(andWhere("statusId", Ops.in(...dataToFilter.status)));
      }

      if (
        dataToFilter?.functionCodes &&
        dataToFilter.functionCodes.length > 0
      ) {
        filters.push(
          andWhere(
            "function.code" as keyof SubRouteAttributes,
            Ops.in(...dataToFilter.functionCodes)
          )
        );
      }

      if (dataToFilter?.seller && dataToFilter.seller.length > 0) {
        filters.push(
          andWhere(
            "mainCollaborator.businessPartner.name" as keyof SubRouteAttributes,
            Ops.in(...dataToFilter.seller)
          )
        );
      }

      return filters;
    };

    // ========== CONSTRUCCIÓN DE OBJETOS JSON ==========

    // Objeto 1: Condiciones base + búsqueda por NAME (o solo condiciones si no hay búsqueda)
    const searchByNameQueryObject: FilterFn<SubRouteAttributes>[] = [
      ...buildBaseFilters({ useOrWhere: false }), // false = usa 'where' (primer objeto)
      hasSearch ? andWhere("name", Ops.like(options.search!, "i")) : noop(),
    ];

    // Objeto 2: Condiciones base + búsqueda por MAIN COLLABORATOR BP NAME (solo si hay búsqueda)
    const searchByMainCollaboratorQueryObject: FilterFn<SubRouteAttributes>[] =
      hasSearch
        ? [
            ...buildBaseFilters({ useOrWhere: true }), // true = usa 'orWhere' (segundo objeto)
            andWhere(
              "mainCollaborator.businessPartner.name" as keyof SubRouteAttributes,
              Ops.like(options.search!, "i")
            ),
          ]
        : [];

    // Objeto 3: Condiciones base + búsqueda por RESPONSIBLE BP NAME (solo si hay búsqueda)
    const searchByResponsibleBpNameQueryObject: FilterFn<SubRouteAttributes>[] =
      hasSearch
        ? [
            ...buildBaseFilters({ useOrWhere: true }),
            andWhere(
              "responsibleBusinessPartner.name" as keyof SubRouteAttributes,
              Ops.like(options.search!, "i")
            ),
          ]
        : [];

    // Objeto 4: Condiciones base + búsqueda por RESPONSIBLE BP EXTERNAL KEYS (solo si hay búsqueda)
    const searchByResponsibleBpCodeQueryObject: FilterFn<SubRouteAttributes>[] =
      hasSearch
        ? [
            ...buildBaseFilters({ useOrWhere: true }),
            andWhere(
              "responsibleBusinessPartner.externalKeys.code" as keyof SubRouteAttributes,
              Ops.like(options.search!, "i")
            ),
          ]
        : [];

    // Objeto 5: Condiciones base + búsqueda por EXTERNAL KEYS CODE (solo si hay búsqueda)
    const searchByExternalKeysQueryObject: FilterFn<SubRouteAttributes>[] =
      hasSearch
        ? [
            ...buildBaseFilters({ useOrWhere: true }),
            andWhere(
              "externalKeys.code" as keyof SubRouteAttributes,
              Ops.like(options.search!, "i")
            ),
          ]
        : [];

    // Objeto 6: Condiciones base + búsqueda por TYPE (solo si hay búsqueda)
    const searchByTypeQueryObject: FilterFn<SubRouteAttributes>[] = hasSearch
      ? [
          ...buildBaseFilters({ useOrWhere: true }),
          andWhere("type", Ops.like(options.search!, "i")),
        ]
      : [];

    // Objeto 7: Condiciones base + búsqueda por ROUTE AREAS NAME (solo si hay búsqueda)
    const searchByRouteAreasQueryObject: FilterFn<SubRouteAttributes>[] =
      hasSearch
        ? [
            ...buildBaseFilters({ useOrWhere: true }),
            andWhere(
              "routeAreas.name" as keyof SubRouteAttributes,
              Ops.like(options.search!, "i")
            ),
          ]
        : [];

    // Objeto 8: Condiciones base + búsqueda por ROUTES NAME (solo si hay búsqueda)
    const searchByRoutesQueryObject: FilterFn<SubRouteAttributes>[] = hasSearch
      ? [
          ...buildBaseFilters({ useOrWhere: true }),
          andWhere(
            "routes.name" as keyof SubRouteAttributes,
            Ops.like(options.search!, "i")
          ),
        ]
      : [];

    // ========== ENSAMBLAJE FINAL ==========

    const query = buildQuery<SubRouteAttributes>(
      // Expandimos los ocho objetos en el buildQuery
      ...searchByNameQueryObject,
      ...searchByMainCollaboratorQueryObject,
      ...searchByResponsibleBpNameQueryObject,
      ...searchByResponsibleBpCodeQueryObject,
      ...searchByExternalKeysQueryObject,
      ...searchByTypeQueryObject,
      ...searchByRouteAreasQueryObject,
      ...searchByRoutesQueryObject,

      // Ordenamiento y paginación
      orderBy(
        options.sort ?? "name",
        options.sortDirection === "ASC" ? OrderVariant.Asc : OrderVariant.Desc
      ),
      modeTree
        ? noop()
        : page(options.currentPage ?? 1, options.limitPerPage ?? 100)
    );

    this.currentFilter = query;

    const params = new URLSearchParams();
    params.set("filter", JSON.stringify(query));

    if (masterUserIds && masterUserIds.length > 0) {
      masterUserIds.forEach((id) => params.append("masterUserIds", id));
    }

    const API_URL = `${apiUrl}api/${moduleName}/withCount?${params.toString()}`;
    return this.http.get<IPaginationResult<SubRouteAttributes>>(API_URL);
  }

  /**
   * Find to combo box
   * @returns Data
   */
  findToComboBox(
    salesOfficeId?
  ): Observable<IRequestResult<SubRouteAttributes[]>> {
    const query = buildQuery<SubRouteAttributes>(
      where("statusId", StatusEnum.ACTIVE),
      andWhere("_deleted", Ops.eq(false)),
      salesOfficeId
        ? andWhere(
            "salesOffice._id" as keyof SubRouteAttributes,
            Ops.in(...[salesOfficeId])
          )
        : noop(),
      select(["id", "name", "externalKeys", "mainCollaborator"]),
      orderBy("id", OrderVariant.Asc)
    );

    const API_URL: string =
      apiUrl +
      `api/${moduleName}/filter?filter=${encodeURIComponent(JSON.stringify(query))}`;
    return this.http.get<IRequestResult<SubRouteAttributes[]>>(API_URL);
  }

  /**
   * Find to combo box
   * @returns Data
   */
  findToComboBoxByGroup(
    groupId?: string[]
  ): Observable<IRequestResult<SubRouteAttributes[]>> {
    const query = buildQuery<SubRouteAttributes>(
      where("statusId", StatusEnum.ACTIVE),
      groupId && groupId.length > 0
        ? andWhere(
            "routeGroup._id" as keyof SubRouteAttributes,
            Ops.in(...groupId)
          )
        : noop(),
      select(["_id", "externalKeys", "id", "name", "externalKeys"]),
      orderBy("name", OrderVariant.Asc)
    );

    const API_URL: string =
      apiUrl +
      `api/${moduleName}/filter?filter=${encodeURIComponent(JSON.stringify(query))}`;
    return this.http.get<IRequestResult<SubRouteAttributes[]>>(API_URL);
  }
  /**
   * Find to combo box
   * @returns Data
   */
  findToComboBoxByCompanyAndModel(
    companyId: string,
    salesOfficeId: string,
    routeGroupId: string,
    model: string
  ): Observable<IRequestResult<SubRouteAttributes[]>> {
    const query = buildQuery<SubRouteAttributes>(
      where("statusId", StatusEnum.ACTIVE),
      andWhere("model", Ops.eq(model)),
      andWhere(
        "company._id" as keyof SubRouteAttributes,
        Ops.eq(companyId, Normalizers.ObjectId)
      ),
      salesOfficeId
        ? andWhere(
            "salesOffice._id" as keyof SubRouteAttributes,
            Ops.eq(salesOfficeId, Normalizers.ObjectId)
          )
        : noop(),
      routeGroupId
        ? andWhere(
            "routeGroup._id" as keyof SubRouteAttributes,
            Ops.eq(routeGroupId, Normalizers.ObjectId)
          )
        : noop(),
      select([
        "id",
        "name",
        "company",
        "routeGroup",
        "externalKeys",
        "salesOffice",
        "routes",
      ]),
      orderBy("name", OrderVariant.Asc)
    );

    const API_URL: string =
      apiUrl +
      `api/${moduleName}/filter?filter=${encodeURIComponent(JSON.stringify(query))}`;
    return this.http.get<IRequestResult<SubRouteAttributes[]>>(API_URL);
  }
  /**
   * Find to combo box
   * @returns Data
   */
  findToComboBoxByGroupAndModel(
    routeId: string[],
    routeAreaId: string[],
    groupId: string[],
    model: string
  ): Observable<IRequestResult<SubRouteAttributes[]>> {
    const query = buildQuery<SubRouteAttributes>(
      where("statusId", StatusEnum.ACTIVE),
      andWhere("model", Ops.eq(model)),
      routeId.length > 0
        ? andWhere("routes._id" as keyof SubRouteAttributes, Ops.in(...routeId))
        : noop(),
      routeAreaId.length > 0
        ? andWhere(
            "routeAreas._id" as keyof SubRouteAttributes,
            Ops.in(...routeAreaId)
          )
        : noop(),
      groupId.length > 0
        ? andWhere(
            "routeGroup._id" as keyof SubRouteAttributes,
            Ops.in(...groupId)
          )
        : noop(),
      select(["id", "name", "externalKeys"]),
      orderBy("name", OrderVariant.Asc)
    );

    const API_URL: string =
      apiUrl +
      `api/${moduleName}/filter?filter=${encodeURIComponent(JSON.stringify(query))}`;
    return this.http.get<IRequestResult<SubRouteAttributes[]>>(API_URL);
  }

  /**
   * Find to combo box
   * @returns Data
   */
  findToMainCollaborator(
    userIds: string[]
  ): Observable<IRequestResult<SubRouteAttributes[]>> {
    const query = buildQuery<SubRouteAttributes>(
      where("statusId", StatusEnum.ACTIVE),
      userIds && userIds.length > 0
        ? andAllWhere(
            "mainCollaborator.masterUserId" as keyof SubRouteAttributes,
            Ops.in(...userIds)
          )
        : noop(),
      // andWhere('mainCollaborator.masterUserId' as keyof SubRouteAttributes,
      //       Ops.eq(userIds, Normalizers.ObjectId)),
      select(["id", "name", "externalKeys", "mainCollaborator"]),
      orderBy("id", OrderVariant.Asc)
    );

    const API_URL: string =
      apiUrl +
      `api/${moduleName}/filter?filter=${encodeURIComponent(JSON.stringify(query))}`;
    return this.http.get<IRequestResult<SubRouteAttributes[]>>(API_URL);
  }

  /**
   * Find to combo box
   * @returns Data
   */
  findToWarehouse(
    warehouseId: string[]
  ): Observable<IRequestResult<SubRouteAttributes[]>> {
    const query = buildQuery<SubRouteAttributes>(
      where("statusId", StatusEnum.ACTIVE),
      warehouseId && warehouseId.length > 0
        ? andAllWhere(
            "warehouses.id" as keyof SubRouteAttributes,
            Ops.in(...warehouseId)
          )
        : noop(),
      select(["id", "name", "externalKeys", "mainCollaborator"]),
      orderBy("id", OrderVariant.Asc)
    );

    const API_URL: string =
      apiUrl +
      `api/${moduleName}/filter?filter=${encodeURIComponent(JSON.stringify(query))}`;
    return this.http.get<IRequestResult<SubRouteAttributes[]>>(API_URL);
  }

  getFileTemplateSubRoute(): string {
    return env.SUB_ROUTE_LOAD_TEMPLATE || null;
  }

  getFileTemplateBpRoute(): string {
    return env.BP_ROUTE_LOAD_TEMPLATE || null;
  }

  importFile(data: any): Observable<any> {
    const API_URL: string = SUB_ROUTE_IMPORT_FILE_FULL_URL;
    return this.http.post(API_URL, data);
  }

  saveFileRoute(file: File, optionLoad: string) {
    const formData = new FormData();
    formData.append("file", file);
    formData.append("transform", optionLoad);
    const API_URL: string = apiUrl + `api/${moduleName}/upload-subroute`;
    return this.http.post<IRequestResult<SubRouteAttributes[]>>(
      API_URL,
      formData
    );
  }

  getRouteExcelData() {
    return env.ROUTES_DATA;
  }

  /**
   * Find to combo box to material list by model
   * @returns Data
   */
  findToComboBoxByModel(
    model?
  ): Observable<IRequestResult<SubRouteAttributes[]>> {
    const query = buildQuery<SubRouteAttributes>(
      where("statusId", StatusEnum.ACTIVE),
      //andWhere("model", Ops.eq(model)),
      select(["_id", "id", "name", "externalKeys"]),
      orderBy("id", OrderVariant.Asc)
    );

    const API_URL: string =
      apiUrl +
      `api/${moduleName}/filter?filter=${encodeURIComponent(JSON.stringify(query))}`;
    return this.http.get<IRequestResult<SubRouteAttributes[]>>(API_URL);
  }

  /**
   * Find to combo box to material list by model
   * @returns Data
   */
  findToComboBoxByPreventa(
    model?,
    routeId?: string[]
  ): Observable<IRequestResult<SubRouteAttributes[]>> {
    const query = buildQuery<SubRouteAttributes>(
      where("statusId", StatusEnum.ACTIVE),
      routeId && routeId.length > 0
        ? andWhere("routes._id" as keyof SubRouteAttributes, Ops.in(...routeId))
        : noop(),
      andWhere("model", Ops.eq(model)),
      select(["_id", "id", "name", "externalKeys"]),
      orderBy("id", OrderVariant.Asc)
    );

    const API_URL: string =
      apiUrl +
      `api/${moduleName}/filter?filter=${encodeURIComponent(JSON.stringify(query))}`;
    return this.http.get<IRequestResult<SubRouteAttributes[]>>(API_URL);
  }

  findToComboBoxByRouteId(
    model?,
    routeId?: number[]
  ): Observable<IRequestResult<SubRouteAttributes[]>> {
    const query = buildQuery<SubRouteAttributes>(
      where("statusId", Ops.eq(StatusEnum.ACTIVE)),
      routeId && routeId.length > 0
        ? andWhere("routes.id" as keyof SubRouteAttributes, Ops.in(...routeId))
        : noop(),
      andWhere("model", Ops.eq(model)),
      select(["_id", "externalKeys", "id", "name", "model", "type"]),
      orderBy("id", OrderVariant.Asc)
    );

    const API_URL: string =
      apiUrl +
      `api/${moduleName}/filter?filter=${encodeURIComponent(JSON.stringify(query))}`;
    return this.http.get<IRequestResult<SubRouteAttributes[]>>(API_URL);
  }

  /**
   * Find sub-routes that are not assigned to any emission point
   * @returns Sub-routes without emission points
   */
  findAllWithoutEmissionPoints(): Observable<
    IRequestResult<SubRouteAttributes[]>
  > {
    const query = buildQuery(
      where("statusId" as keyof SubRouteAttributes, Ops.eq(StatusEnum.ACTIVE))
    );

    const API_URL: string =
      apiUrl +
      `api/${moduleName}/filterWithoutEmissionPoints?filter=${encodeURIComponent(JSON.stringify(query))}`;
    return this.http.get<IRequestResult<SubRouteAttributes[]>>(API_URL);
  }

  /**
   * Find to main collaborator for sub-route
   * @returns Data
   */
  findToUserSubRoute(
    masterUserId?
  ): Observable<IRequestResult<SubRouteAttributes[]>> {
    const query = buildQuery<SubRouteAttributes>(
      where("statusId", StatusEnum.ACTIVE),
      andWhere("_deleted", Ops.eq(false)),
      masterUserId
        ? andWhere(
            "mainCollaborator.masterUserId" as keyof SubRouteAttributes,
            Ops.eq(masterUserId)
          )
        : noop(),
      select(["id", "name", "externalKeys", "mainCollaborator"]),
      orderBy("id", OrderVariant.Asc)
    );

    const API_URL: string =
      apiUrl +
      `api/${moduleName}/filter?filter=${encodeURIComponent(JSON.stringify(query))}`;
    return this.http.get<IRequestResult<SubRouteAttributes[]>>(API_URL);
  }
}
